import { Guid } from "guid-typescript";
import React, { ImgHTMLAttributes, PureComponent, ReactNode, createRef } from "react";
import { findDOMNode } from "react-dom";
import { Controlled as ControlledZoom } from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import { DEFAULT_IMAGE } from "../../../shared/utilty/constants";
import { KMainFunctions } from "../../../shared/utilty/main-functions";
import { CommonProps } from "../common-props";
import { DashboardState } from "../kuika-cl-model-runtimes";

declare let window: Window & { kuika: any };

interface ImageProps extends ImgHTMLAttributes<any> {
  value?: string;
  style?: React.CSSProperties;
  placeHolderImage?: string;
  onChange?: (value: any) => void;
  zoomOnClick?: boolean;
  position?: "bottom" | "center" | "right" | "left" | "top";
  imageFit?: "fill" | "fit" | "stretch";
}

interface ImageState {
  guidID: string;
  isZoomed: boolean;
  isLoading: boolean;
}

class Image extends PureComponent<ImageProps & CommonProps, ImageState> {
  private errorCountPerUrlMap: Map<string, number> = new Map<string, number>();

  private imageRef = createRef<HTMLImageElement>();

  private parentHeight: number = 0;

  constructor(props: ImageProps) {
    super(props);
    this.state = {
      guidID: "",
      isZoomed: false,
      isLoading: false
    };
  }

  componentDidMount() {
    const node = findDOMNode(this) as HTMLDivElement;
    const isReportDesigner = window?.kuika?.dashboardState === DashboardState.reportDesigner;

    if (node && node.parentElement) {
      this.parentHeight = node.parentElement.clientHeight;

      if (isReportDesigner) {
        node.style.width = `${this.props.style?.width ?? "100%"}`;
        node.style.height = `${this.props.style?.height ?? "100%"}`;
      }
      if (node.parentElement) {
        this.applyParentStyles(node.parentElement);
      }
    }
  }

  componentDidUpdate(prevProps: Readonly<ImageProps & CommonProps>) {
    if (prevProps.value !== this.props.value) {
      this.errorCountPerUrlMap.clear();
    }

    if (this.imageRef.current) {
      const node = findDOMNode(this) as HTMLDivElement;
      if (node && node.parentElement) {
        this.applyParentStyles(node.parentElement);
      }
    }
  }

  applyParentStyles = (element: HTMLElement) => {
    const isReportDesigner = window?.kuika?.dashboardState === DashboardState.reportDesigner;
    if (!element || isReportDesigner) return;
    const display = this.props.style?.display;

    element.style.lineHeight = "0";
    if (window?.kuika?.isDesignTime) {
      element.style.fontSize = "0";
      element.style.verticalAlign = "top";
      element.style.margin = "0";
      element.style.padding = "0";
      element.style.display = display === "block" ? "block" : "inline-block";
      if (this.props.style?.width) {
        element.style.width = this.props.style?.width.toString();
      }
      if (this.props.style?.height) {
        element.style.height = this.props.style?.height.toString();
      }
    }
  };

  adjustImageHeight = () => {
    const img = this.imageRef.current;
    if (!img) return;

    const hasSpecifiedHeight = this.props.style?.height !== undefined;
    const specifiedHeight = this.getImageHeight();

    if (hasSpecifiedHeight && specifiedHeight > 0) {
      img.style.height = `${specifiedHeight}px`;
    } else if (this.parentHeight > 0 && img.naturalHeight > 0) {
      const imgHeight = img.clientHeight;
      if (this.parentHeight > imgHeight) {
        img.style.height = `${this.parentHeight}px`;
      }
    }
  };

  onChange = (e) => {
    if (this.props.onChange) {
      this.props.onChange(e);
    }
  };

  getStyleProp = () => {
    let style: React.CSSProperties = {};
    const isReportDesigner = window?.kuika?.dashboardState === DashboardState.reportDesigner;
    const isDesignTime = window?.kuika?.isDesignTime;

    if (this.props.style) {
      style = { ...this.props.style };
    }

    if (this.props.showCursorPointer) {
      style.cursor = "pointer";
    }

    if (this.props.position) {
      style.objectPosition = this.props.position;
    }

    if (this.props.imageFit) {
      if (this.props.imageFit === "fill") {
        style.objectFit = "cover";
      } else if (this.props.imageFit === "fit") {
        style.objectFit = "contain";
      } else if (this.props.imageFit === "stretch" && isReportDesigner) {
        style.objectFit = "fill";
      }
    }

    if (style.display === "inline") {
      style.display = "inline-block";
    }

    if (!style.height) {
      style.verticalAlign = "middle";
    }

    return style;
  };

  getClassName = () => {
    const isDesignTime = window?.kuika?.isDesignTime;
    let result = "";

    if (this.props.className) {
      result = this.props.className;
    }

    if (!isDesignTime) result += " kuika-image-input ";
    return result;
  };

  private getImageFitValue = () => {
    switch (this.props.imageFit) {
      case "fill":
        return 1;
      case "stretch":
        return 2;
      case "fit":
      default:
        return 0;
    }
  };

  private getImageHeight = (): number => {
    // Only an absolute pixel height is a real forced height. Percentage /
    // viewport units (100%, 50vh, ...) must stay CSS-driven, so they resolve to
    // 0 ("unset") instead of being mistaken for a pixel value (KOA-18064).
    return KMainFunctions.resolveResizePixels(this.props.style?.height) ?? 0;
  };

  getValue = (): string => {
    return KMainFunctions.resolveImageSrc(this.props.value, {
      width: this.props.style?.width,
      height: this.props.style?.height,
      imageFit: this.getImageFitValue(),
      placeHolderImage: this.props.placeHolderImage,
      cacheBust: true
    });
  };

  isPlaceholderImageApplicable = () => {
    return this.props.placeHolderImage && this.props.placeHolderImage !== "";
  };

  onError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (!(e.target instanceof HTMLImageElement)) return;

    const currentErrorCountOfThisUrl: number = this.errorCountPerUrlMap.get(e.currentTarget.src) ?? 0;

    // If higher than 1, it means it has already been tried to fallback to placeholder image & failed.
    if (currentErrorCountOfThisUrl >= 1) {
      e.currentTarget.onerror = null;
      e.currentTarget.src = DEFAULT_IMAGE;
      return;
    }

    this.errorCountPerUrlMap.set(e.currentTarget.src, currentErrorCountOfThisUrl + 1);

    let imageToFallback: string = DEFAULT_IMAGE;
    if (this.isPlaceholderImageApplicable()) {
      const placeholderGuid: string = Guid.parse(this.props.placeHolderImage).toString();

      if (placeholderGuid !== Guid.EMPTY) {
        imageToFallback = KMainFunctions.getUrlByWithResourceId(
          placeholderGuid,
          this.props.style?.width?.toString()?.replace("px", "") ?? 0,
          this.props.style?.height?.toString()?.replace("px", "") ?? 0,
          this.getImageFitValue()
        );
      }

      e.target.src = imageToFallback;
      return;
    }

    e.target.src = imageToFallback;
  };

  render(): ReactNode {
    if (this.props.zoomOnClick && !window?.kuika?.isDesignTime) {
      return (
        <ControlledZoom
          isZoomed={this.state.isZoomed}
          onZoomChange={(shouldZoom) => {
            this.setState({ isZoomed: shouldZoom });
            if (!shouldZoom && this.props.onClick) {
              this.props.onClick(null);
            }
          }}
        >
          <img
            ref={this.imageRef}
            id={this.props.id}
            draggable={!window?.kuika?.isDesignTime}
            onMouseEnter={this.props.onMouseEnter}
            onMouseLeave={this.props.onMouseLeave}
            onClickCapture={() => this.setState({ isZoomed: !this.state.isZoomed })}
            className={this.getClassName()}
            onChange={this.onChange}
            src={this.getValue()}
            style={this.getStyleProp()}
            onError={this.onError}
          />
        </ControlledZoom>
      );
    }

    return (
      <img
        ref={this.imageRef}
        id={this.props.id}
        draggable={!window?.kuika?.isDesignTime}
        onMouseEnter={this.props.onMouseEnter}
        onMouseLeave={this.props.onMouseLeave}
        className={this.getClassName()}
        onChange={this.onChange}
        src={this.getValue()}
        style={this.getStyleProp()}
        onClick={this.props.onClick}
        onError={this.onError}
      />
    );
  }
}

const image = withCommonEvents(Image);
export { image as Image };
